<template>
	<div class="clipboard-toast">
		<p>We couldn't copy the {{ artifactType }} link automatically. Copy it below instead:</p>
		<input
			type="text"
			:value="url"
			readonly
			autocomplete="off"
			class="clipboard-text-field-native"
			aria-label="Copyable link"
			v-test="'clipboard-toast-field'" />
		<button
			v-test="'clipboard-toast-copy-button'"
			v-track="'clipboard-toast-copy-button'"
			class="clipboard-copy-button-native"
			:disabled="copying"
			@click="copyLink">
			Copy
		</button>
	</div>
</template>

<script>
	import { calculateTimeout, copyToClipboard } from "@/helpers/utilities";
	import { logWarn } from "@/helpers/logError";
	import { useToast } from "vue-toastification";

	export default {
		setup() {
			const toast = useToast();
			return { toast };
		},
		data() {
			return {
				// True while a copy attempt is in flight. Without it, double-tapping Copy could land a
				// stale failure after a newer attempt had already succeeded and closed this toast,
				// contradicting the success the user just saw. Disabling an in-flight async action is
				// the sanctioned pattern here (root CLAUDE.md, "Form action buttons") — this guards a
				// double-submit, it does not gate on validity.
				copying: false,
				// Set in beforeUnmount. Every write that happens after the await below is gated on it:
				// once this toast is gone, so is the field its failure message points at, and "select
				// the link above" would send the user to a control that no longer exists.
				isUnmounted: false
			};
		},
		beforeUnmount() {
			this.isUnmounted = true;
		},
		props: {
			url: {
				type: String,
				required: true
			},
			artifactType: {
				type: String,
				default: "media"
			},
			onClose: {
				type: Function,
				required: false
			}
		},
		methods: {
			async copyLink() {
				if (this.copying) {
					logWarn(`[CopyLink] Ignoring a Copy tap for the ${this.artifactType} link: a copy is already in flight.`, {
						artifactType: this.artifactType
					});
					return;
				}

				this.copying = true;

				let copied = false;
				try {
					copied = await copyToClipboard(this.url);
				} finally {
					// Cleared on every exit, including a throw, so one failure cannot wedge the button
					// permanently and leave the user with a link they can no longer copy.
					this.copying = false;
				}

				if (this.isUnmounted) {
					// Nothing here can be reported against UI the user has already dismissed, so the
					// outcome goes to the log instead of a toast that would reference a vanished field.
					logWarn(`[CopyLink] Discarding a ${this.artifactType} link copy result: the manual-copy toast was dismissed mid-flight.`, {
						artifactType: this.artifactType,
						copied
					});
					return;
				}

				if (copied) {
					const message = `The ${this.artifactType} link has been copied to the clipboard.`;
					this.toast.success(message, { timeout: calculateTimeout(message) });
					if (this.onClose) {
						this.onClose();
					}
				} else {
					// Informational, not an error: the link is already displayed above, so
					// selecting it by hand is a real path forward rather than a dead end.
					const message = `We still couldn't copy the ${this.artifactType} link. Select the link above to copy it yourself.`;
					this.toast.info(message, { timeout: calculateTimeout(message) });
					logWarn(`[CopyLink] Manual clipboard copy failed for the ${this.artifactType} link.`, { artifactType: this.artifactType });
				}
			}
		}
	};
</script>

<style scoped>
	.clipboard-toast {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 10px;
	}

	.clipboard-text-field-native {
		width: 100%;
		background-color: #fff;
		border: 1px solid #888888;
		border-radius: 8px;
		padding: 8px 12px;
		color: #888888;
		font-size: 1rem;
		outline: none;
	}

	.clipboard-copy-button-native {
		background-color: #fff;
		color: #2196f3;
		font-size: 0.875rem;
		font-weight: 600;
		padding: 6px 16px;
		border-radius: 24px;
		border: none;
		cursor: pointer;
		/* mimic v-btn elevation=0 */
		box-shadow: none;
	}

	.clipboard-copy-button-native:hover {
		background-color: #f5f5f5;
	}
</style>
